import { apiCall } from '../../services/apiUtil';

/**
 * Profile API
 *
 * Handles user profile operations:
 * - Get user profile
 * - Update user profile
 * - Delete user account
 * - User preferences
 */
export const profileAPI = {
  /**
   * Get user by ID
   * @param {number} id - User ID
   * @returns {Promise} User data
   */
  getUserById: async (id) => {
    return apiCall(`/user/${id}`);
  },

  /**
   * Get all users (for search, etc.)
   * @returns {Promise} Array of users
   */
  getAll: async () => {
    return apiCall('/user');
  },

  /**
   * Update user profile
   * @param {number} id - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise} Updated user data
   */
  update: async (id, userData) => {
    return apiCall(`/user/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  },

  /**
   * Delete user account (soft delete)
   * @param {number} userId - User ID
   * @param {string} password - User password for confirmation
   * @returns {Promise} API response
   */
  deleteAccount: async (userId, password) => {
    return apiCall(`/user/${userId}/delete-account`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  /**
   * Get user preferences (notification settings, etc.)
   * @param {number} userId - User ID
   * @returns {Promise} User preferences
   */
  getPreferences: async (userId) => {
    return apiCall(`/user/${userId}/preferences`);
  },

  /**
   * Update user preferences
   * @param {number} userId - User ID
   * @param {Object} preferencesData - Updated preferences
   * @returns {Promise} Updated preferences
   */
  updatePreferences: async (userId, preferencesData) => {
    return apiCall(`/user/${userId}/preferences`, {
      method: 'PUT',
      body: JSON.stringify(preferencesData),
    });
  },

  /**
   * Update notification settings
   * @param {number} userId - User ID
   * @param {Object} settings - Notification settings
   * @returns {Promise} Updated settings
   */
  updateNotificationSettings: async (userId, settings) => {
    return apiCall(`/user/${userId}/notification-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  /**
   * Get security questions for password recovery
   * @param {string} email - User email
   * @returns {Promise} Security questions
   */
  getSecurityQuestions: async (email) => {
    return apiCall(`/user/security-questions?email=${encodeURIComponent(email)}`);
  },

  /**
   * Verify security answers
   * @param {string} email - User email
   * @param {Object} answers - Security answers
   * @returns {Promise} Verification result
   */
  verifySecurityAnswers: async (email, answers) => {
    return apiCall('/user/verify-security-answers', {
      method: 'POST',
      body: JSON.stringify({ email, ...answers }),
    });
  },

  /**
   * Update security questions
   * @param {number} userId - User ID
   * @param {Object} securityData - New security questions and answers
   * @returns {Promise} API response
   */
  updateSecurityQuestions: async (userId, securityData) => {
    return apiCall(`/user/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(securityData),
    });
  },
};

export default profileAPI;
